import { useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Drawer, Typography } from 'antd';
import {
  UserDeleteOutlined,
  ClockCircleOutlined,
  MessageOutlined,
  EllipsisOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { NO_SHOW_REASONS } from '../../../types/provider';
import type { NoShowReason } from '../../../types/provider';

interface Props {
  open: boolean;
  loading: boolean;
  onPick: (reason: NoShowReason) => void;
  onCancel: () => void;
}

const REASON_KEY: Record<NoShowReason, string> = {
  'Mijoz kelmadi':            'reason1',
  'Mijoz bilan kelisholmadik': 'reason2',
  'Boshqa sabab':             'reason3',
};

const REASON_CONFIG: Record<
  NoShowReason,
  { icon: ReactNode; color: string }
> = {
  'Mijoz kelmadi':            { icon: <ClockCircleOutlined />, color: '#fa8c16' },
  'Mijoz bilan kelisholmadik': { icon: <MessageOutlined />,     color: '#1677ff' },
  'Boshqa sabab':             { icon: <EllipsisOutlined />,    color: '#8c8c8c' },
};

// ─── Reason row ─────────────────────────────────────────────────────────────

function ReasonRow({
  icon,
  color,
  label,
  loading,
  isSelected,
  disabled,
  onClick,
}: {
  icon: ReactNode;
  color: string;
  label: string;
  loading: boolean;
  isSelected: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  const [isPressed, setIsPressed] = useState(false);
  const showSpinner = loading && isSelected;

  return (
    <button
      type="button"
      onClick={() => !disabled && onClick()}
      disabled={disabled}
      onTouchStart={() => !disabled && setIsPressed(true)}
      onTouchEnd={() => setIsPressed(false)}
      onTouchCancel={() => setIsPressed(false)}
      onMouseDown={() => !disabled && setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onMouseLeave={() => setIsPressed(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        padding: '12px 14px',
        borderRadius: 14,
        border: `1px solid ${isPressed ? color + '40' : 'var(--bl-divider, #ebebeb)'}`,
        background: isPressed ? color + '08' : 'var(--bl-surface, #fff)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled && !isSelected ? 0.5 : 1,
        transform: isPressed ? 'scale(0.985)' : 'scale(1)',
        transition: 'background 0.15s ease, border 0.15s ease, transform 0.1s ease',
        WebkitTapHighlightColor: 'transparent',
        outline: 'none',
        textAlign: 'left',
      }}
    >
      {/* Icon disc */}
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: 11,
          background: `linear-gradient(135deg, ${color}14 0%, ${color}24 100%)`,
          border: `1px solid ${color}1a`,
          color,
          fontSize: 16,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {showSpinner ? <LoadingOutlined spin /> : icon}
      </div>

      {/* Label */}
      <Typography.Text
        style={{
          flex: 1,
          fontSize: 14,
          fontWeight: 600,
          color: 'var(--bl-text-strong, #0f172a)',
          lineHeight: 1.35,
        }}
      >
        {label}
      </Typography.Text>
    </button>
  );
}

// ─── Main dialog ────────────────────────────────────────────────────────────

const HEADER_ICON_COLOR = '#ff4d4f';

export default function NoShowReasonDialog({ open, loading, onPick, onCancel }: Props) {
  const { t } = useTranslation('provider');
  const [pickedReason, setPickedReason] = useState<NoShowReason | null>(null);

  const handlePick = (reason: NoShowReason) => {
    setPickedReason(reason);
    onPick(reason);
  };

  const handleAfterOpenChange = (visible: boolean) => {
    if (!visible) setPickedReason(null);
  };

  const cancelStyle: CSSProperties = {
    width: '100%',
    height: 46,
    border: 'none',
    background: 'var(--bl-surface-soft, #f7f8fa)',
    color: 'var(--bl-text-muted, #6b7280)',
    fontSize: 14,
    fontWeight: 600,
    borderRadius: 12,
    cursor: loading ? 'not-allowed' : 'pointer',
    WebkitTapHighlightColor: 'transparent',
    opacity: loading ? 0.6 : 1,
    transition: 'background 0.15s ease',
  };

  return (
    <Drawer
      open={open}
      placement="bottom"
      onClose={onCancel}
      closable={false}
      maskClosable={!loading}
      height="auto"
      afterOpenChange={handleAfterOpenChange}
      styles={{
        body: {
          padding: '12px 16px',
          paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
          background: 'var(--bl-surface, #fff)',
        },
        wrapper: { borderRadius: '20px 20px 0 0', overflow: 'hidden' },
        mask: { background: 'rgba(15,23,42,0.45)' },
      }}
    >
      {/* Drag handle */}
      <div
        aria-hidden
        style={{
          width: 36,
          height: 4,
          borderRadius: 2,
          background: 'var(--bl-divider, #e5e7eb)',
          margin: '0 auto 18px',
        }}
      />

      {/* Header: icon disc + title + hint */}
      <div style={{ textAlign: 'center', marginBottom: 18 }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${HEADER_ICON_COLOR}14 0%, ${HEADER_ICON_COLOR}26 100%)`,
            border: `1px solid ${HEADER_ICON_COLOR}1f`,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 24,
            color: HEADER_ICON_COLOR,
            margin: '0 auto 12px',
            boxShadow: `0 6px 18px ${HEADER_ICON_COLOR}1a`,
          }}
        >
          <UserDeleteOutlined />
        </div>
        <Typography.Text
          style={{
            fontSize: 17,
            fontWeight: 700,
            display: 'block',
            color: 'var(--bl-text-strong, #0f172a)',
            marginBottom: 4,
            letterSpacing: -0.2,
          }}
        >
          {t('bookings.noShowDialog.title')}
        </Typography.Text>
        <Typography.Text
          style={{
            fontSize: 13,
            color: 'var(--bl-text-muted, #6b7280)',
            display: 'block',
            maxWidth: 280,
            margin: '0 auto',
            lineHeight: 1.45,
          }}
        >
          {t('bookings.noShowDialog.hint')}
        </Typography.Text>
      </div>

      {/* Reason rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
        {NO_SHOW_REASONS.map((reason) => {
          const config = REASON_CONFIG[reason];
          const label = t(`noShow.reasons.${REASON_KEY[reason]}`, { defaultValue: reason });
          return (
            <ReasonRow
              key={reason}
              icon={config.icon}
              color={config.color}
              label={label}
              loading={loading}
              isSelected={pickedReason === reason}
              disabled={loading}
              onClick={() => handlePick(reason)}
            />
          );
        })}
      </div>

      {/* Cancel */}
      <button
        type="button"
        onClick={() => !loading && onCancel()}
        disabled={loading}
        style={cancelStyle}
      >
        {t('bookings.noShowDialog.cancelBtn')}
      </button>
    </Drawer>
  );
}
